/**
 * المرحلة 1.4 — the visit page inside «عرض Live».
 *
 * The patient in the room has a booking; the visit opens from it with one
 * tap, and its note and vital signs are written right here, under the three
 * consultation actions, without leaving the queue. The whole page — the
 * diagnosis with ICD-10, the papers, the history — is one link away.
 *
 * Nothing renders where the Super Admin closed the visit page for the
 * institution (the `encounters` switch), so the live view stays what it was.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Spinner } from '../../components/common';
import { encounterApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useNotifications } from '../../context/NotificationContext';
import { clinicalShown } from '../../domain/clinicalPolicy';
import { useI18n } from '../../i18n/I18nProvider';
import { useEncounter } from './useEncounter';
import { EncounterStatus } from './EncounterStatus';
import { SoapEditor } from './SoapEditor';
import { VitalsPanel } from './VitalsPanel';

function LiveBody({ initial, autocomplete }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const hook = useEncounter(initial.id, { initial });
  const e = hook.encounter;
  const [part, setPart] = useState('note');
  if (!e) return null;
  return (
    <div data-testid="live-encounter">
      <div className="enc-live-head">
        <h3>
          <Icon name="clipboard-pen" size={18} className="text-cyan-300" />
          {t('encounter.live.title')}
        </h3>
        <EncounterStatus status={e.status} />
        <Button variant="subtle" icon="external-link" className="enc-live-open" onClick={() => navigate(`/doctor/encounters/${e.id}`)} data-testid="live-encounter-open">
          {t('encounter.live.full')}
        </Button>
      </div>
      <div className="vit-charts mb-3" role="group" aria-label={t('encounter.live.title')}>
        <button type="button" aria-pressed={part === 'note'} onClick={() => setPart('note')}>
          {t('encounter.tabs.note')}
        </button>
        <button type="button" aria-pressed={part === 'vitals'} onClick={() => setPart('vitals')}>
          {t('encounter.tabs.vitals')}
          {(e.vitals || []).length ? ` (${e.vitals.length})` : ''}
        </button>
      </div>
      {part === 'note' ? <SoapEditor encounter={e} write={hook.write} autocomplete={autocomplete} compact /> : <VitalsPanel encounter={e} write={hook.write} compact />}
      {hook.reasonDialog}
    </div>
  );
}

export function LiveEncounterPanel({ booking, autocomplete = null }) {
  const { t } = useI18n();
  const { locale } = usePermissions();
  const { error: toastError, success } = useNotifications();
  const [state, setState] = useState({ loading: false, encounter: null, canOpen: false });
  const [opening, setOpening] = useState(false);
  const bookingId = booking ? String(booking.id) : null;
  const shown = clinicalShown(locale, 'encounters');

  // Keyed on the booking's id: the live view polls, and each poll is a new object for the same patient.
  useEffect(() => {
    if (!bookingId || !shown) {
      setState({ loading: false, encounter: null, canOpen: false });
      return undefined;
    }
    let cancelled = false;
    setState({ loading: true, encounter: null, canOpen: false });
    encounterApi
      .byBooking(bookingId)
      .then((res) => !cancelled && setState({ loading: false, encounter: res.encounter || null, canOpen: Boolean(res.canOpen) }))
      .catch(() => !cancelled && setState({ loading: false, encounter: null, canOpen: false }));
    return () => {
      cancelled = true;
    };
  }, [bookingId, shown]);

  if (!shown || !bookingId) return null;
  // Read only (or not the doctor's to open) and nothing written yet: nothing to show.
  if (!state.loading && !state.encounter && !state.canOpen) return null;

  const start = async () => {
    setOpening(true);
    try {
      const res = await encounterApi.open({ bookingId });
      setState({ loading: false, encounter: res.encounter, canOpen: true });
      success(t('encounter.live.opened'));
    } catch (err) {
      toastError(err.message);
    } finally {
      setOpening(false);
    }
  };

  return (
    <section className="glass-panel enc-live rounded-2xl p-5">
      {state.loading ? (
        <Spinner variant="inline" label={t('encounter.loading')} />
      ) : state.encounter ? (
        <LiveBody key={state.encounter.id} initial={state.encounter} autocomplete={autocomplete} />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="flex items-center gap-2 text-base font-bold text-white">
              <Icon name="clipboard-pen" size={18} className="text-cyan-300" />
              {t('encounter.live.title')}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-slate-400">{t('encounter.live.hint')}</p>
          </div>
          <Button icon="clipboard-plus" loading={opening} onClick={start} data-testid="live-encounter-start">
            {t('encounter.live.start')}
          </Button>
        </div>
      )}
    </section>
  );
}

export default LiveEncounterPanel;
